import { type RefCallback, useLayoutEffect, useRef, useState } from "react";

interface ChipFit {
  /**
   * The chips this count was measured for.
   */
  key: string;
  /**
   * How many leading chips fit on the trigger's line.
   */
  count: number;
}

interface FittingChipsParams {
  /**
   * Measurement key per chip, in trigger order.
   */
  chipKeys: string[];
  /**
   * Measured width per chip key.
   */
  widths: Map<string, number>;
  /**
   * Width available to the chip row.
   */
  rowWidth: number;
  /**
   * Measured width of the "+N" badge, `0` until it has been rendered once.
   */
  badgeWidth: number;
  /**
   * Gap between chips.
   */
  gap: number;
}

/**
 * Leading chips that fit on one line, keeping room for the "+N" badge
 * whenever something is left over. Never less than one — a chip wider than
 * the trigger shrinks instead of vanishing.
 */
function countFittingChips({
  chipKeys,
  widths,
  rowWidth,
  badgeWidth,
  gap
}: FittingChipsParams): number {
  // Nothing is measurable while the trigger has no layout (hidden, or a
  // zero-layout test environment) — keep the one-chip form.
  if (!rowWidth) {
    return 1;
  }

  let used = 0;
  let count = 0;
  for (const chipKey of chipKeys) {
    const width = widths.get(chipKey);
    if (width === undefined) {
      break;
    }
    const line = used + (count > 0 ? gap : 0) + width;
    const rest = chipKeys.length - count - 1;
    if (line + (rest > 0 ? gap + badgeWidth : 0) > rowWidth) {
      break;
    }
    used = line;
    count += 1;
  }
  return Math.max(count, 1);
}

/**
 * Re-fit whenever the trigger is resized.
 */
function useRowWidth(row: HTMLDivElement | null): number {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (!row) {
      return;
    }
    setWidth(row.clientWidth);
    const observer = new ResizeObserver(() => setWidth(row.clientWidth));
    observer.observe(row);
    return () => observer.disconnect();
  }, [row]);

  return width;
}

/**
 * Fits the selected-value chips to the trigger: as many whole chips as the
 * line holds, the rest summarised by the "+N" badge. The fit lands before the
 * browser paints.
 *
 * `chipKeys` holds one key per chip, carrying everything that changes its
 * width — the label it renders, plus whatever else it draws. A changed key
 * re-measures the row; an unchanged one does not.
 *
 * Put `rowRef` on the element holding the chips — the effect reads its
 * children, the "+N" badge last.
 */
export function useFittedChips(chipKeys: string[]): {
  rowRef: RefCallback<HTMLDivElement>;
  visibleCount: number;
} {
  const [row, setRow] = useState<HTMLDivElement | null>(null);
  const widths = useRef(new Map<string, number>());
  const [badgeWidth, setBadgeWidth] = useState(0);
  const [fit, setFit] = useState<ChipFit | null>(null);
  const rowWidth = useRowWidth(row);

  const key = chipKeys.join("\u0000");
  // Until the fit lands, lay the chips out so the effect can size them — but
  // never more than a trigger could hold. A pasted column of 500 values would
  // otherwise mount 500 chips just to drop all but a handful.
  const visibleCount =
    fit?.key === key ? fit.count : Math.min(chipKeys.length, 30);

  useLayoutEffect(() => {
    if (!row || !key) {
      return;
    }

    // Rebuilt from `key` so a fresh array identity alone doesn't re-measure.
    const keys = key.split("\u0000");
    const nodes = Array.from(row.children) as HTMLElement[];
    const overflows = visibleCount < keys.length;

    if (overflows) {
      setBadgeWidth(nodes[nodes.length - 1].offsetWidth);
    }
    // A lone chip may shrink, so its width isn't its own to cache.
    if (visibleCount > 1) {
      const chips = overflows ? nodes.slice(0, -1) : nodes;
      chips.forEach((chip, index) =>
        widths.current.set(keys[index], chip.offsetWidth)
      );
    }

    const count = countFittingChips({
      chipKeys: keys,
      widths: widths.current,
      rowWidth: row.clientWidth,
      badgeWidth,
      gap: parseFloat(getComputedStyle(row).columnGap) || 0
    });
    setFit((previous) =>
      previous?.key === key && previous.count === count
        ? previous
        : { key, count }
    );
  }, [row, key, rowWidth, badgeWidth, visibleCount]);

  return { rowRef: setRow, visibleCount };
}
